import { TrashIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { YStack } from "tamagui";
import {
  RELATIONSHIP_TYPES,
  RelatedUserRole,
} from "../../api/relationship.type";
import { useContactsContext } from "../../hooks/ContactsContextWrapper";
import { useUserContext } from "../../hooks/UserContextWrapper";
import { SubmitButton } from "../shared/buttons/submit-button";
import Card from "../shared/card";
import { ConfirmationModal } from "../shared/ConfirmationModal";
import PhoneInput from "../shared/phone-input";
import TamaguiInput from "../shared/tamagui-input";
import ThemedSelect from "../shared/themed-select";
import Typography from "../shared/typography";

export const UpdateContact = () => {
  const { selectedContact, remove, isLoading } = useContactsContext();
  const { update, isUpdating } = useUserContext();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [relatedUserRole, setRelatedUserRole] =
    useState<RelatedUserRole | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (selectedContact) {
      setFirstName(selectedContact.contact.related_user.first_name || "");
      setLastName(selectedContact.contact.related_user.last_name || "");
      setEmail(selectedContact.contact.related_user.email || "");
      setPhone(selectedContact.contact.related_user.phone || "");
      setRelatedUserRole(
        (selectedContact.contact.related_user_role as RelatedUserRole) || null
      );
    }
  }, [selectedContact]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      await update({
        user: {
          id: selectedContact?.contact.related_user.id as string,
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
        },
        contactId: selectedContact?.contact.id,
        relatedUserRole: relatedUserRole || undefined,
      });
      navigate("/contacts");
    } catch (err) {
      console.error(err);
      setError("Failed to update contact");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Card
        className="w-full max-w-md mx-auto"
        variant="cream"
        elevation={2}
        hoverEffect
      >
        <Card.Header className="text-center" variant="cream">
          <Typography variant="h3" className="text-primary">
            Update Contact
          </Typography>
        </Card.Header>
        <Card.Body>
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
              <Typography variant="body2">{error}</Typography>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <YStack gap="$3">
              <TamaguiInput
                id="firstName"
                label="First Name"
                value={firstName}
                onChangeText={setFirstName}
                placeholder="Enter first name"
                fullWidth
              />
              <TamaguiInput
                id="lastName"
                label="Last Name"
                value={lastName}
                onChangeText={setLastName}
                placeholder="Enter last name"
                fullWidth
              />
              <TamaguiInput
                id="email"
                label="Email"
                inputType="email"
                value={email}
                onChangeText={setEmail}
                placeholder="Enter email"
                fullWidth
              />
              <PhoneInput
                id="phone"
                label="Phone"
                value={phone}
                onChangeText={setPhone}
                placeholder="Enter phone"
                fullWidth
              />
              <ThemedSelect
                placeholder={
                  relatedUserRole ? relatedUserRole : "Select relationship type"
                }
                label="Relationship Type"
                value={relatedUserRole || ""}
                onChange={(e) =>
                  setRelatedUserRole(e.target.value as RelatedUserRole)
                }
                options={RELATIONSHIP_TYPES.map((type) => ({
                  value: type.type,
                  label: type.type,
                }))}
                initialValue={relatedUserRole || undefined}
              />

              <div className="pt-2">
                <SubmitButton
                  isLoading={isLoading || isUpdating}
                  onClick={() => {}}
                  onSubmit={handleSubmit}
                  isDisabled={!firstName || !lastName || !email || !phone}
                  iconOnly={false}
                  size="md"
                  className="w-full"
                >
                  Update Contact
                </SubmitButton>
              </div>
              <div className="pt-2">
                <SubmitButton
                  type="button"
                  color="beige"
                  onClick={() => navigate(-1)}
                  iconOnly={false}
                  size="md"
                  className="w-full"
                  hideArrow={true}
                >
                  Cancel
                </SubmitButton>
              </div>
              <div className="pt-2">
                <SubmitButton
                  type="button"
                  color="cream"
                  icon={<TrashIcon className="w-4 h-4" />}
                  onClick={() => setIsDeleteModalOpen(true)}
                  iconOnly={false}
                  size="md"
                  className="w-full"
                  hideArrow={true}
                >
                  Remove Contact
                </SubmitButton>
              </div>
            </YStack>
          </form>
        </Card.Body>
      </Card>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          if (selectedContact?.contact.id) {
            remove(selectedContact.contact.id);
          }
          navigate("/contacts");
        }}
        title="Delete Contact"
        message={`Are you sure you want to delete ${firstName} ${lastName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        tone="danger"
      />
    </div>
  );
};
